import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../utils/api';

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [cities, setCities] = useState([]);
  const [niches, setNiches] = useState([]);
  const [services, setServices] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);
  const [selectedNiches, setSelectedNiches] = useState([]);
  const [otherCity, setOtherCity] = useState('');
  const [showOtherCity, setShowOtherCity] = useState(false);
  const [scraping, setScraping] = useState(false);
  const [scrapeResult, setScrapeResult] = useState(null);
  const [search, setSearch] = useState('');
  const [filterHot, setFilterHot] = useState(false);
  const [filterNiche, setFilterNiche] = useState('');
  const [filterService, setFilterService] = useState('');
  const [error, setError] = useState('');
  const [pushingAll, setPushingAll] = useState(false);

  const loadLeads = useCallback(() => {
    api.getLeads().then(data => setLeads(Array.isArray(data) ? data : [])).catch(e => setError(e.message));
  }, []);

  useEffect(() => {
    loadLeads();
    api.getCities().then(setCities).catch(() => {});
    api.getNiches().then(setNiches).catch(() => {});
    api.getServices().then(setServices).catch(() => {});
  }, [loadLeads]);

  const handleScrape = async () => {
    setScraping(true);
    setScrapeResult(null);
    setError('');
    try {
      const citiesToSend = [...selectedCities];
      if (showOtherCity && otherCity.trim()) citiesToSend.push(otherCity.trim());
      const result = await api.scrape(citiesToSend, selectedNiches);
      setScrapeResult(result);
      loadLeads();
    } catch (e) {
      setError(e.message);
    } finally {
      setScraping(false);
    }
  };

  const handlePush = async (leadId) => {
    try {
      await api.pushToProspect(leadId);
      alert('Pushed to prospects!');
    } catch (e) {
      alert(e.message);
    }
  };

  const handlePushAll = async () => {
    if (!window.confirm(`Push all ${filtered.length} leads to prospects?`)) return;
    setPushingAll(true);
    try {
      const result = await api.pushAllToProspects();
      alert(`Pushed ${result.pushed} new leads to prospects! (${result.total} total prospects)`);
    } catch (e) {
      alert(e.message);
    } finally {
      setPushingAll(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this lead?')) return;
    await api.deleteLead(id);
    loadLeads();
  };

  const toggleCity = (city) => {
    setSelectedCities(prev =>
      prev.includes(city) ? prev.filter(c => c !== city) : [...prev, city]
    );
  };

  const toggleNiche = (niche) => {
    setSelectedNiches(prev =>
      prev.includes(niche) ? prev.filter(n => n !== niche) : [...prev, niche]
    );
  };

  const filtered = leads.filter(l => {
    if (search && !(l.name || '').toLowerCase().includes(search.toLowerCase()) && !(l.suburb || '').toLowerCase().includes(search.toLowerCase())) return false;
    if (filterHot && !l.isHot) return false;
    if (filterNiche && l.niche !== filterNiche) return false;
    if (filterService && l.suggestedService !== filterService) return false;
    return true;
  });

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">Leads</h2>

      {/* Scraper Controls */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 mb-6">
        <h3 className="text-lg font-semibold mb-3">Google Maps Scraper</h3>

        {/* Cities — multi-select */}
        <div className="mb-4">
          <p className="text-sm text-gray-400 mb-2">Cities <span className="text-gray-600 text-xs">(select multiple)</span></p>
          <div className="flex flex-wrap gap-2 mb-2">
            {cities.map(c => (
              <button
                key={c}
                onClick={() => toggleCity(c)}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                  selectedCities.includes(c) ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-700 text-gray-400 hover:border-gray-500'
                }`}
              >
                {c}
              </button>
            ))}
            <button
              onClick={() => setShowOtherCity(!showOtherCity)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                showOtherCity ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-700 text-gray-400 hover:border-gray-500'
              }`}
            >
              + Other
            </button>
          </div>
          {showOtherCity && (
            <input
              type="text"
              value={otherCity}
              onChange={e => setOtherCity(e.target.value)}
              placeholder="Type city name..."
              className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-blue-500 w-48"
            />
          )}
          {selectedCities.length > 0 && (
            <p className="text-xs text-blue-400 mt-1">Selected: {selectedCities.join(', ')}{showOtherCity && otherCity ? `, ${otherCity}` : ''}</p>
          )}
        </div>

        {/* Niches — multi-select */}
        <div className="mb-4">
          <p className="text-sm text-gray-400 mb-2">Niches <span className="text-gray-600 text-xs">(select multiple)</span></p>
          <div className="flex flex-wrap gap-2">
            {niches.map(n => (
              <button
                key={n}
                onClick={() => toggleNiche(n)}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                  selectedNiches.includes(n) ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-700 text-gray-400 hover:border-gray-500'
                }`}
              >
                {n}
              </button>
            ))}
          </div>
          {selectedNiches.length > 0 && (
            <p className="text-xs text-blue-400 mt-1">Selected: {selectedNiches.join(', ')}</p>
          )}
        </div>

        <button
          onClick={handleScrape}
          disabled={scraping || selectedCities.length === 0 || selectedNiches.length === 0}
          className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:text-gray-500 text-white px-6 py-2.5 rounded-lg font-medium transition-colors"
        >
          {scraping ? 'Scraping... (this takes a few minutes)' : 'Start Scraping'}
        </button>
        {scrapeResult && (
          <p className="mt-3 text-sm text-green-400">
            Found {scrapeResult.newCount} new leads!
          </p>
        )}
        {error && <p className="mt-3 text-sm text-red-400">{error}</p>}
      </div>

      {/* Service Filter */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 mb-6">
        <h3 className="text-lg font-semibold mb-3">Filter by Service Need</h3>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setFilterService('')}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
              !filterService ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-700 text-gray-400 hover:border-gray-500'
            }`}
          >
            All Services
          </button>
          {services.map(s => (
            <button
              key={s}
              onClick={() => setFilterService(filterService === s ? '' : s)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                filterService === s ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-700 text-gray-400 hover:border-gray-500'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <input
          type="text"
          placeholder="Search leads..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500 w-64"
        />
        <button
          onClick={() => setFilterHot(!filterHot)}
          className={`text-sm px-4 py-2 rounded-lg border transition-colors ${
            filterHot ? 'bg-red-600/20 border-red-600 text-red-400' : 'border-gray-700 text-gray-400 hover:border-gray-500'
          }`}
        >
          Hot Leads Only
        </button>
        <select
          value={filterNiche}
          onChange={e => setFilterNiche(e.target.value)}
          className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
        >
          <option value="">All Niches</option>
          {niches.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
        <span className="text-sm text-gray-500 self-center">
          {filtered.length} leads
        </span>
        {filtered.length > 0 && (
          <button
            onClick={handlePushAll}
            disabled={pushingAll}
            className="ml-auto text-sm px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-700 disabled:text-gray-500 text-white rounded-lg font-medium transition-colors"
          >
            {pushingAll ? 'Pushing...' : `Push All ${filtered.length} to Prospects`}
          </button>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-gray-800">
        <table className="w-full text-sm">
          <thead className="bg-gray-900">
            <tr className="text-left text-gray-400">
              <th className="px-4 py-3 font-medium">Business</th>
              <th className="px-4 py-3 font-medium">Owner</th>
              <th className="px-4 py-3 font-medium">Phone</th>
              <th className="px-4 py-3 font-medium">Location</th>
              <th className="px-4 py-3 font-medium">Niche</th>
              <th className="px-4 py-3 font-medium">Rating</th>
              <th className="px-4 py-3 font-medium">Reviews</th>
              <th className="px-4 py-3 font-medium">Website</th>
              <th className="px-4 py-3 font-medium">Suggestion</th>
              <th className="px-4 py-3 font-medium">Why?</th>
              <th className="px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {filtered.map(lead => (
              <tr key={lead.id} className={`${lead.isHot ? 'bg-red-950/20' : ''} hover:bg-gray-900/50`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    {lead.isHot && <span className="w-2 h-2 rounded-full bg-red-500 inline-block flex-shrink-0" title="Hot lead" />}
                    <span className="font-medium">{lead.name}</span>
                  </div>
                  {lead.email && <div className="text-xs text-gray-500 mt-0.5">{lead.email}</div>}
                </td>
                <td className="px-4 py-3">
                  {lead.ownerName ? (
                    <div className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-green-500 inline-block flex-shrink-0" title="Owner name found" />
                      <span className="text-xs text-gray-300">{lead.ownerName}</span>
                    </div>
                  ) : (
                    <span className="text-gray-600 text-xs">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className="text-xs text-gray-400">{lead.phone || '—'}</span>
                </td>
                <td className="px-4 py-3 text-gray-400 text-xs">{lead.suburb}, {lead.city}</td>
                <td className="px-4 py-3 text-gray-400 text-xs">{lead.niche}</td>
                <td className="px-4 py-3">
                  {lead.rating ? (
                    <span className="text-yellow-400 text-xs">&#9733; {lead.rating}</span>
                  ) : (
                    <span className="text-gray-600 text-xs">N/A</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs ${lead.reviewCount < 30 ? 'text-orange-400 font-medium' : 'text-gray-400'}`}>
                    {lead.reviewCount}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {lead.website === 'No website' ? (
                    <span className="text-red-400 text-xs px-2 py-0.5 bg-red-500/10 rounded-full">No website</span>
                  ) : (
                    <a href={lead.website} target="_blank" rel="noreferrer" className="text-blue-400 hover:underline text-xs truncate block max-w-[140px]">
                      {(lead.website || '').replace(/https?:\/\/(www\.)?/, '').split('/')[0]}
                    </a>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className="text-xs px-2 py-1 rounded-full bg-blue-500/10 text-blue-400">
                    {lead.suggestedService}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-xs text-gray-500 max-w-[180px] block">
                    {lead.serviceReason || '—'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <button
                      onClick={() => handlePush(lead.id)}
                      className="text-xs px-3 py-1.5 bg-green-600/20 text-green-400 rounded-lg hover:bg-green-600/30 transition-colors"
                    >
                      Prospect
                    </button>
                    <button
                      onClick={() => handleDelete(lead.id)}
                      className="text-xs px-2 py-1.5 text-gray-500 hover:text-red-400 transition-colors"
                    >
                      ✕
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={11} className="px-4 py-8 text-center text-gray-500">No leads found. Run the scraper to get started.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
